/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { AuthService, ITurnAroundTimeSearchFilters } from 'app/um/services/auth-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { I18N } from 'aurelia-i18n';
import { localSort } from 'app/shared/lib/sorting';
import { Toaster } from 'app/shared/lib/toaster';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { getReference } from 'app/shared/models/essette-types';
import { deepClone } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';

interface CustomTurnAroundTime extends UM.AuthServiceTurnAroundTime {
    valueWithUnitText?: string;
}

@autoinject()
export class ManageServiceLineTurnAroundTimesPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    protected turnAroundTimes: CustomTurnAroundTime[] = [];
    protected allTurnAroundTimes: CustomTurnAroundTime[] = [];
    protected editedTurnAroundTime: CustomTurnAroundTime | null = null;

    protected linesOfBusiness: ReadonlyArray<UM.AuthLineOfBusiness> = [];
    protected types: ReadonlyArray<UM.AuthType> = [];
    protected priorities: ReadonlyArray<UM.AuthPriority> = [];
    protected statuses: ReadonlyArray<UM.AuthServiceStatus> = [];
    protected baseStatuses: ReadonlyArray<UM.AuthServiceStatus> = [];

    protected selectedLineOfBusiness: UM.AuthLineOfBusiness | null = null;
    protected selectedType: UM.AuthType | null = null;
    protected selectedPriority: UM.AuthPriority | null = null;

    protected resultCount: number = 0;
    protected pageSize: number = 10;

    private sorting = new Sorting();
    protected validate = validate;

    ////****************** Element References ***************************************////

    protected $modal!: IPopup;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        protected i18n: I18N,
        protected service: AuthService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        const turnAroundTimes: CustomTurnAroundTime[] = <CustomTurnAroundTime[]>this.allTurnAroundTimes;
        if(direction === null) {
            this.sorting.remove(property);
        }
        else {
            this.sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        if(this.sorting.entries.length) localSort(turnAroundTimes, sorting);
        else {
            let defaultSort: Sorting = new Sorting();
            defaultSort.add({ reference: 'status.description', direction: SortDirection.Ascending });
            localSort(turnAroundTimes, defaultSort);
        }

        this.loadTatResultsPage();
    }

    protected formSuccess(): void {
        //Update the edited line in the table
        this.turnAroundTimes.forEach(t => {
            if(t.status?.id == this.editedTurnAroundTime?.status?.id) {
                t.id = this.editedTurnAroundTime?.id;
                t.value = this.editedTurnAroundTime?.value;
                t.unit = this.editedTurnAroundTime?.unit;
                t.baseStatus = this.editedTurnAroundTime?.baseStatus;
                if(t.unit && t.value) t.valueWithUnitText = `${t.value} ${this.getUnitTextTranslationString(t.unit, t.value)}`;
            }
        });

        this.$modal.close();
    }

    ////****************** Functions ************************************************////

    public async reload(): Promise<void> {
        await this.loadLookups();
    }

    protected async submitAuthProperties(): Promise<void> {
        await this.loadTurnAroundTimes();
    }

    protected saveTurnAroundTime(turnAroundTime: CustomTurnAroundTime = {}): void {
        turnAroundTime.lineOfBusiness = getReference(this.selectedLineOfBusiness);
        turnAroundTime.type = getReference(this.selectedType);
        turnAroundTime.priority = getReference(this.selectedPriority);
        this.editedTurnAroundTime = deepClone(turnAroundTime);
        if(turnAroundTime.status?.flag?.isInitial) this.editedTurnAroundTime.baseStatus = turnAroundTime.status;
        this.$modal.open();
    }

    protected getUnitTextTranslationString(unitCode: string, count: number): string {
        switch(unitCode) {
            case "B":
                return this.i18n.tr('ui:timespans.businessDays.$', { count });
            case "C":
                return this.i18n.tr('ui:timespans.days.$', { count });
            case "H":
                return this.i18n.tr('ui:timespans.hours.$', { count });
            case "R":
                return this.i18n.tr('ui:timespans.businessHours.$', { count });
            case "S":
                return this.i18n.tr('ui:timespans.businessDaysWSaturday.$', { count });
            default:
                return "";
        }
    }

    protected getPaginatedTatResults(): CustomTurnAroundTime[] {
        if(this.allTurnAroundTimes.length === 0) return [];
        const startIndex = (this.$pager?.page || 0) * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.allTurnAroundTimes.slice(startIndex, endIndex);
    }

    protected loadTatResultsPage(): void {
        this.turnAroundTimes = this.getPaginatedTatResults();
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = { all: true };

        try {
            this.state.beginLoad();

            const [linesOfBusiness, types, priorities, statuses] = await Promise.all(
                [
                    this.service.loadAuthLinesOfBusiness(),
                    this.service.loadAuthTypes(true),
                    this.service.loadAuthPriorities(),
                    this.service.loadAuthServiceStatuses(options)
                ]);

            this.linesOfBusiness = linesOfBusiness.results;
            this.types = types.results;
            this.priorities = priorities.results;
            this.statuses = statuses.results.filter(s => s.flag?.isOpen);
            this.baseStatuses = statuses.results.filter(s => s.flag?.isTurnAroundTimeBase);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadTurnAroundTimes(): Promise<void> {
        try {
            this.state.beginLoad();

            this.turnAroundTimes = [] as CustomTurnAroundTime[];
            this.allTurnAroundTimes = [] as CustomTurnAroundTime[];

            // restore default sort if no other sorts specified
            if(!this.sorting.entries.length) {
                this.sorting.entries.push({
                    reference: 'status.description',
                    direction: SortDirection.Ascending
                });
            }

            const options: Partial<IQueryLoadOptions> = {
                sorting: this.sorting,
                all: true
            };
            const includes = ['status', 'baseStatus'];
            const filters: ITurnAroundTimeSearchFilters = {
                lineOfBusiness: this.selectedLineOfBusiness,
                type: this.selectedType,
                priority: this.selectedPriority
            };

            const turnAroundTimesResponse = await this.service.loadAuthServiceTurnAroundTimes(options, includes, filters);
            const turnAroundTimes = <CustomTurnAroundTime[]>turnAroundTimesResponse.results;

            turnAroundTimes.forEach(tat => {
                // translate unit ('B', 'C', etc.) into appropriate text ('Business Days', 'Calendar Days', etc.)
                if(tat.unit && tat.value) tat.valueWithUnitText = `${tat.value} ${this.getUnitTextTranslationString(tat.unit, tat.value)}`;
            });

            this.statuses.forEach(s => {
                const tat = turnAroundTimes.find(t => t.status?.id == s.id);
                if(tat) this.allTurnAroundTimes.push(tat);
                else {
                    this.allTurnAroundTimes.push({ status: { id: s.id, description: s.description, code: s.code, $display: s.$display, flag: { isInitial: s.flag?.isInitial } } });
                }
            });

            // set results for pager
            this.resultCount = this.allTurnAroundTimes.length || 0;
            this.loadTatResultsPage();

            // remove sorting so local sort can take over
            this.sorting.entries = [];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deleteTurnAroundTime(turnAroundTime: UM.AuthServiceTurnAroundTime): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAuthServiceTurnAroundTime(turnAroundTime.id!, false);
            isSuccessful = response.isSuccessful;

            //Clear tat data in the table
            if(isSuccessful) {
                this.allTurnAroundTimes.forEach(t => {
                    if(t.id == turnAroundTime.id) {
                        t.id = null;
                        t.value = null;
                        t.unit = null;
                        t.baseStatus = null;
                        t.valueWithUnitText = undefined;
                    }
                });
                this.loadTatResultsPage();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.deleted('um:auth.turnAroundTime.$long', isSuccessful);
            this.state.endSave();
        }
    }

}
